import { API, BACKEND_URL } from '@/config/api';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { isDemoUser } from '@/lib/utils';
import AdminNavigation from '@/components/AdminNavigation';
import AreaCard from './AdminLocations/AreaCard';
import LocationCard from './AdminLocations/LocationCard';
import PlaceAutocomplete from './AdminLocations/PlaceAutocomplete';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, Plus, Edit2, Trash2, Building2, Loader2, Search, Map as MapIcon, Navigation, CheckCircle, X } from 'lucide-react';
import { APIProvider, Map as GoogleMap, AdvancedMarker } from '@vis.gl/react-google-maps';

export default function AdminLocations() {
  const { user } = useAuth();
  const [areas, setAreas] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [areaDialogOpen, setAreaDialogOpen] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [editAreaDialogOpen, setEditAreaDialogOpen] = useState(false);
  const [editLocationDialogOpen, setEditLocationDialogOpen] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 59.25, lng: 5.8 });
  const [markerPosition, setMarkerPosition] = useState(null);
  const [addressValue, setAddressValue] = useState('');
  const [editMarkerPosition, setEditMarkerPosition] = useState(null);
  const [editAddressValue, setEditAddressValue] = useState('');
  const [editMapKey, setEditMapKey] = useState(0);
  
  const [newAreaData, setNewAreaData] = useState({
    name: '',
    region: ''
  });
  
  const [newLocationData, setNewLocationData] = useState({
    letter: '',
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    area_id: ''
  });
  
  const [editAreaData, setEditAreaData] = useState({
    name: '',
    region: ''
  });
  
  const [editLocationData, setEditLocationData] = useState({
    letter: '',
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    area_id: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [areasRes, locationsRes] = await Promise.all([
        axios.get(`${API}/areas`, { withCredentials: true }),
        axios.get(`${API}/locations`, { withCredentials: true })
      ]);
      setAreas(areasRes.data);
      setLocations(locationsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Fallback to mock data if API fails
      setAreas([
        { id: 'p02-rogaland', name: 'P02 Rogaland', region: 'Rogaland' },
        { id: 'p03-rogaland', name: 'P03 Rogaland', region: 'Rogaland' },
        { id: 'p04-hordaland', name: 'P04 Hordaland', region: 'Hordaland' },
        { id: 'p05-sogn', name: 'P05 Sogn og Fjordane', region: 'Sogn og Fjordane' },
      ]);
      setLocations([
        { id: 'loc-a', letter: 'A', name: 'Toftøy', latitude: 59.28, longitude: 5.72, area_id: 'p02-rogaland' },
        { id: 'loc-b', letter: 'B', name: 'Føresvik', latitude: 59.32, longitude: 5.68, area_id: 'p02-rogaland' },
        { id: 'loc-c', letter: 'C', name: 'Rosshl.', latitude: 59.24, longitude: 5.75, area_id: 'p02-rogaland' },
        { id: 'loc-d', letter: 'D', name: 'Høgsfj.', latitude: 59.20, longitude: 5.80, area_id: 'p02-rogaland' },
        { id: 'loc-e', letter: 'E', name: 'Finnøyfj.', latitude: 59.35, longitude: 5.65, area_id: 'p02-rogaland' },
        { id: 'loc-f', letter: 'F', name: 'Årdalsfj.', latitude: 59.18, longitude: 5.85, area_id: 'p02-rogaland' },
        { id: 'loc-g', letter: 'G', name: 'Jøsenfj.', latitude: 59.22, longitude: 5.78, area_id: 'p02-rogaland' },
        { id: 'loc-h', letter: 'H', name: 'Erfj.', latitude: 59.30, longitude: 5.70, area_id: 'p02-rogaland' },
        { id: 'loc-i', letter: 'I', name: 'Jelsa', latitude: 59.26, longitude: 5.82, area_id: 'p02-rogaland' },
        { id: 'loc-j', letter: 'J', name: 'Vindafj.', latitude: 59.16, longitude: 5.88, area_id: 'p02-rogaland' },
        { id: 'loc-k', letter: 'K', name: 'Rott', latitude: 59.34, longitude: 5.66, area_id: 'p02-rogaland' },
        { id: 'loc-l', letter: 'L', name: 'Boknafj.', latitude: 59.19, longitude: 5.76, area_id: 'p02-rogaland' },
      ]);
      setLoading(false);
    }
  }, []);

  // Fetch data on mount
  useEffect(() => {
    const loadData = async () => {
      await fetchData();
    };
    loadData();
  }, [fetchData]);

  const addArea = async () => {
    if (!newAreaData.name || !newAreaData.region) {
      toast.error('Alle felter er påkrevd');
      return;
    }

    try {
      await axios.post(`${API}/areas`, newAreaData, { withCredentials: true });
      toast.success('Område opprettet');
      setAreaDialogOpen(false);
      setNewAreaData({ name: '', region: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating area:', error);
      toast.error('Kunne ikke opprette område');
    }
  };

  const addLocation = async () => {
    if (!newLocationData.letter || !newLocationData.name || !newLocationData.area_id) {
      toast.error('Bokstav, navn og område er påkrevd');
      return;
    }
    
    if (!newLocationData.latitude || !newLocationData.longitude) {
      toast.error('Vennligst velg posisjon på kartet');
      return;
    }

    try {
      await axios.post(`${API}/locations`, newLocationData, { withCredentials: true });
      toast.success('Lokalitet opprettet');
      setLocationDialogOpen(false);
      setNewLocationData({ letter: '', name: '', address: '', latitude: '', longitude: '', area_id: '' });
      setMarkerPosition(null);
      setAddressValue('');
      fetchData();
    } catch (error) {
      console.error('Error creating location:', error);
      toast.error('Kunne ikke opprette lokalitet');
    }
  };

  const handlePlaceSelect = (place) => {
    setMapCenter({ lat: place.lat, lng: place.lng });
    setMarkerPosition({ lat: place.lat, lng: place.lng });
    setNewLocationData({
      ...newLocationData,
      address: place.address,
      latitude: place.lat.toFixed(6),
      longitude: place.lng.toFixed(6)
    });
  };

  const handleMapClick = (e) => {
    if (!e.detail || !e.detail.latLng) return;
    
    const lat = e.detail.latLng.lat;
    const lng = e.detail.latLng.lng;
    
    setMarkerPosition({ lat, lng });
    setNewLocationData({
      ...newLocationData,
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6)
    });
  };

  const handleMarkerDrag = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    
    setMarkerPosition({ lat, lng });
    setNewLocationData({
      ...newLocationData,
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6)
    });
  };

  const openEditAreaDialog = (area) => {
    setEditingArea(area);
    setEditAreaData({
      name: area.name,
      region: area.region
    });
    setEditAreaDialogOpen(true);
  };

  const updateArea = async () => {
    if (!editAreaData.name || !editAreaData.region) {
      toast.error('Alle felter er påkrevd');
      return;
    }

    try {
      await axios.put(`${API}/areas/${editingArea.id}`, editAreaData, { withCredentials: true });
      toast.success('Område oppdatert');
      setEditAreaDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error updating area:', error);
      toast.error('Kunne ikke oppdatere område');
    }
  };

  const openEditLocationDialog = (location) => {
    setEditingLocation(location);
    setEditLocationData({
      letter: location.letter,
      name: location.name,
      latitude: location.latitude?.toString() || '',
      longitude: location.longitude?.toString() || '',
      area_id: location.area_id
    });
    setEditLocationDialogOpen(true);
  };

  const updateLocation = async () => {
    if (!editLocationData.letter || !editLocationData.name || !editLocationData.area_id) {
      toast.error('Bokstav, navn og område er påkrevd');
      return;
    }

    try {
      await axios.put(`${API}/locations/${editingLocation.id}`, editLocationData, { withCredentials: true });
      toast.success('Lokalitet oppdatert');
      setEditLocationDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error updating location:', error);
      toast.error('Kunne ikke oppdatere lokalitet');
    }
  };

  const deleteArea = async (areaId) => {
    // Check if area has locations
    const hasLocations = locations.some(loc => loc.area_id === areaId);
    if (hasLocations) {
      toast.error('Kan ikke slette område med tilknyttede lokaliteter');
      return;
    }

    if (!window.confirm('Er du sikker på at du vil slette dette området?')) {
      return;
    }

    try {
      await axios.delete(`${API}/areas/${areaId}`, { withCredentials: true });
      toast.success('Område slettet');
      fetchData();
    } catch (error) {
      console.error('Error deleting area:', error);
      toast.error('Kunne ikke slette område');
    }
  };

  const deleteLocation = async (locationId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne lokaliteten?')) {
      return;
    }

    try {
      await axios.delete(`${API}/locations/${locationId}`, { withCredentials: true });
      toast.success('Lokalitet slettet');
      fetchData();
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error('Kunne ikke slette lokalitet');
    }
  };

  const getLocationsByArea = (areaId) => {
    const filtered = locations.filter(loc => loc.area_id === areaId);
    if (locations.length > 0 && filtered.length === 0) {
    }
    return filtered;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-teal-50/20 flex">
        <AdminNavigation />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-teal-600 mx-auto mb-3" />
            <p className="text-slate-600 text-sm">Laster...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-teal-50/20 flex">
      <AdminNavigation />
      <main className="flex-1 p-4 lg:p-8 overflow-x-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">
                  Områder & Lokaliteter
                </h1>
                <p className="text-sm text-slate-600">Administrer overvåkingsområder og prøvestasjonar</p>
              </div>
            </div>
          </div>

          {/* Areas Section */}
          <div className="mb-10">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="text-xl lg:text-2xl font-bold text-slate-900">Overvåkingsområder</h2>
                <p className="text-sm text-slate-600">Definer geografiske områder for prøvetaking</p>
              </div>
              <Dialog open={areaDialogOpen} onOpenChange={setAreaDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Nytt område
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Legg til nytt område</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Områdenavn *</Label>
                      <Input
                        value={newAreaData.name}
                        onChange={(e) => setNewAreaData({ ...newAreaData, name: e.target.value })}
                        placeholder="P02 Rogaland"
                      />
                    </div>
                    <div>
                      <Label>Region *</Label>
                      <Input
                        value={newAreaData.region}
                        onChange={(e) => setNewAreaData({ ...newAreaData, region: e.target.value })}
                        placeholder="Rogaland"
                      />
                    </div>
                    <div className="flex space-x-2 pt-4">
                      <Button onClick={addArea} className="flex-1 bg-blue-600 hover:bg-blue-700">
                        Opprett område
                      </Button>
                      <Button variant="outline" onClick={() => setAreaDialogOpen(false)} className="flex-1">
                        Avbryt
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {areas.map((area) => {
                const areaLocations = getLocationsByArea(area.id);
                const hasLocations = areaLocations.length > 0;
                
                return (
                  <AreaCard
                    key={area.id}
                    area={area}
                    locationCount={areaLocations.length}
                    locationsWithGPS={areaLocations.filter(l => l.latitude && l.longitude).length}
                    onEdit={openEditAreaDialog}
                    onDelete={deleteArea}
                    hasLocations={hasLocations}
                  />
                );
              })}
            </div>
          </div>

          {/* Edit Area Dialog */}
          <Dialog open={editAreaDialogOpen} onOpenChange={setEditAreaDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Rediger område</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Områdenavn *</Label>
                  <Input
                    value={editAreaData.name}
                    onChange={(e) => setEditAreaData({ ...editAreaData, name: e.target.value })}
                    placeholder="P02 Rogaland"
                  />
                </div>
                <div>
                  <Label>Region *</Label>
                  <Input
                    value={editAreaData.region}
                    onChange={(e) => setEditAreaData({ ...editAreaData, region: e.target.value })}
                    placeholder="Rogaland"
                  />
                </div>
                <div className="flex space-x-2 pt-4">
                  <Button onClick={updateArea} className="flex-1 bg-blue-600 hover:bg-blue-700">
                    Lagre endringer
                  </Button>
                  <Button variant="outline" onClick={() => setEditAreaDialogOpen(false)} className="flex-1">
                    Avbryt
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Locations Section */}
          <div>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-1">Prøvestasjonar (A-L)</h2>
                <p className="text-slate-600">Spesifikke lokaliteter for bulk-registrering</p>
              </div>
              <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-teal-600 hover:bg-teal-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Ny lokalitet
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
                  <DialogHeader className="pb-6 border-b border-slate-200">
                    <DialogTitle className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">
                      Opprett ny lokalitet
                    </DialogTitle>
                    <p className="text-slate-600 mt-2">Fyll inn informasjon og velg nøyaktig posisjon på kartet</p>
                  </DialogHeader>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 py-6">
                    {/* Left side: Form fields */}
                    <div className="space-y-6">
                      {/* Step 1: Basic info */}
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2 mb-4">
                          <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-blue-600 rounded-lg text-white text-sm flex items-center justify-center font-bold">1</div>
                          <h3 className="text-lg font-bold text-slate-900">Grunnleggende info</h3>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-sm font-semibold text-slate-700 mb-2 block">Bokstav *</Label>
                            <Input
                              value={newLocationData.letter}
                              onChange={(e) => setNewLocationData({ ...newLocationData, letter: e.target.value.toUpperCase() })}
                              placeholder="A"
                              maxLength={1}
                              className="h-14 text-2xl font-bold text-center border-2 border-slate-200 focus:border-teal-500 rounded-xl"
                            />
                          </div>
                          <div>
                            <Label className="text-sm font-semibold text-slate-700 mb-2 block">Navn *</Label>
                            <Input
                              value={newLocationData.name}
                              onChange={(e) => setNewLocationData({ ...newLocationData, name: e.target.value })}
                              placeholder="f.eks. Toftøy"
                              className="h-14 text-base border-2 border-slate-200 focus:border-teal-500 rounded-xl"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-semibold text-slate-700 mb-2 block">Tilhører område *</Label>
                          <Select 
                            value={newLocationData.area_id} 
                            onValueChange={(value) => setNewLocationData({ ...newLocationData, area_id: value })}
                          >
                            <SelectTrigger className="h-14 border-2 border-slate-200 focus:border-teal-500 rounded-xl">
                              <SelectValue placeholder="Velg overvåkingsområde" />
                            </SelectTrigger>
                            <SelectContent>
                              {areas.map((area) => (
                                <SelectItem key={area.id} value={area.id}>
                                  <div className="flex items-center space-x-2">
                                    <MapIcon className="w-4 h-4 text-blue-600" />
                                    <span>{area.name}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* Step 2: Address search */}
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2 mb-4">
                          <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-blue-600 rounded-lg text-white text-sm flex items-center justify-center font-bold">2</div>
                          <h3 className="text-lg font-bold text-slate-900">Finn stasjon</h3>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-semibold text-slate-700 mb-2 block">Søk adresse eller sted</Label>
                          <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
                            <PlaceAutocomplete
                              onPlaceSelect={handlePlaceSelect}
                              value={addressValue}
                              onChange={setAddressValue}
                            />
                          </APIProvider>
                        </div>
                        
                        {newLocationData.address && (
                          <div className="p-4 bg-teal-50 border-2 border-teal-200 rounded-xl animate-in slide-in-from-top duration-300">
                            <div className="flex items-start space-x-3">
                              <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                                <MapPin className="w-5 h-5 text-white" />
                              </div>
                              <div className="flex-1">
                                <div className="text-xs font-semibold text-teal-700 uppercase tracking-wide mb-1">Valgt adresse</div>
                                <div className="text-sm text-slate-900 font-medium">{newLocationData.address}</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Step 3: Coordinates */}
                      {markerPosition && (
                        <div className="space-y-4 animate-in slide-in-from-bottom duration-500">
                          <div className="flex items-center space-x-2 mb-4">
                            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg text-white text-sm flex items-center justify-center font-bold">✓</div>
                            <h3 className="text-lg font-bold text-emerald-700">Posisjon valgt!</h3>
                          </div>
                          
                          <div className="p-6 bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl">
                            <div className="grid grid-cols-2 gap-4">
                              <div className="bg-white rounded-xl p-4 border border-emerald-200">
                                <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Breddegrad</div>
                                <div className="text-xl font-mono font-bold text-slate-900">
                                  {newLocationData.latitude}°N
                                </div>
                              </div>
                              <div className="bg-white rounded-xl p-4 border border-emerald-200">
                                <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Lengdegrad</div>
                                <div className="text-xl font-mono font-bold text-slate-900">
                                  {newLocationData.longitude}°E
                                </div>
                              </div>
                            </div>
                            
                            <div className="mt-4 p-3 bg-white/50 rounded-lg">
                              <div className="flex items-center space-x-2 text-xs text-emerald-700">
                                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                                <span className="font-semibold">GPS-koordinater bekreftet</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Right side: Interactive Map */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg text-white text-sm flex items-center justify-center font-bold">
                            <MapIcon className="w-4 h-4" />
                          </div>
                          <h3 className="text-lg font-bold text-slate-900">Interaktivt kart</h3>
                        </div>
                        {markerPosition && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setMarkerPosition(null);
                              setNewLocationData({ ...newLocationData, latitude: '', longitude: '', address: '' });
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Fjern pin
                          </Button>
                        )}
                      </div>
                      
                      <div className="relative h-[550px] rounded-2xl overflow-hidden border-4 border-slate-200 shadow-2xl">
                        <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
                          <GoogleMap
                            defaultCenter={mapCenter}
                            defaultZoom={10}
                            mapId="location-selector-map-new"
                            onClick={handleMapClick}
                            gestureHandling="greedy"
                            disableDefaultUI={false}
                            zoomControl={true}
                            mapTypeControl={true}
                            fullscreenControl={true}
                            streetViewControl={false}
                            scrollwheel={true}
                            styles={[
                              {
                                featureType: 'water',
                                elementType: 'geometry',
                                stylers: [{ color: '#b3e5fc' }]
                              },
                              {
                                featureType: 'landscape',
                                elementType: 'geometry',
                                stylers: [{ color: '#f5f5f5' }]
                              }
                            ]}
                          >
                            {markerPosition && (
                              <AdvancedMarker
                                position={markerPosition}
                                draggable={true}
                                onDragEnd={handleMarkerDrag}
                              >
                                <div className="relative animate-in zoom-in duration-500">
                                  {/* Main pin */}
                                  <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-600 rounded-full border-4 border-white shadow-2xl flex items-center justify-center cursor-move hover:scale-125 transition-all duration-300">
                                    <MapPin className="w-8 h-8 text-white" />
                                  </div>
                                  {/* Pin shadow */}
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-10 bg-gradient-to-b from-teal-600/60 to-transparent rounded-full"></div>
                                  {/* Pulsing ring */}
                                  <div className="absolute inset-0 w-16 h-16 bg-teal-400 rounded-full animate-ping opacity-30"></div>
                                  {/* Static ring */}
                                  <div className="absolute inset-0 w-16 h-16 border-4 border-teal-400/30 rounded-full"></div>
                                </div>
                              </AdvancedMarker>
                            )}
                          </GoogleMap>
                        </APIProvider>
                        
                        {/* Floating instructions */}
                        {!markerPosition && (
                          <div className="absolute top-6 left-1/2 transform -translate-x-1/2 z-10 max-w-sm">
                            <div className="bg-white/95 backdrop-blur-md rounded-2xl px-6 py-4 shadow-2xl border-2 border-slate-200 animate-in fade-in slide-in-from-top-4 duration-700">
                              <div className="text-center">
                                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                                  <MapPin className="w-6 h-6 text-white" />
                                </div>
                                <p className="text-sm font-semibold text-slate-900 mb-1">Klikk på kartet</p>
                                <p className="text-xs text-slate-600">for å plassere lokalitetens pin</p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Map controls hint */}
                        <div className="absolute bottom-4 left-4 z-10">
                          <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg border border-slate-200 text-xs text-slate-600">
                            <div className="flex items-center space-x-2">
                              <Navigation className="w-3 h-3 text-teal-600" />
                              <span>Scroll for zoom • Dra for å flytte</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Instructions */}
                      <div className="grid grid-cols-3 gap-3 text-center">
                        <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
                          <div className="text-2xl mb-1">🔍</div>
                          <div className="text-xs font-semibold text-blue-900">Søk adresse</div>
                        </div>
                        <div className="p-3 bg-teal-50 rounded-xl border border-teal-200">
                          <div className="text-2xl mb-1">🖱️</div>
                          <div className="text-xs font-semibold text-teal-900">Klikk på kart</div>
                        </div>
                        <div className="p-3 bg-purple-50 rounded-xl border border-purple-200">
                          <div className="text-2xl mb-1">✋</div>
                          <div className="text-xs font-semibold text-purple-900">Dra pin</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action buttons */}
                  <div className="flex space-x-3 pt-6 border-t border-slate-200">
                    <Button 
                      onClick={addLocation} 
                      className="flex-1 h-14 bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white font-bold text-base rounded-xl shadow-lg hover:shadow-xl transition-all"
                      disabled={!newLocationData.letter || !newLocationData.name || !newLocationData.area_id || !markerPosition}
                    >
                      {!markerPosition ? (
                        <>
                          <MapPin className="w-5 h-5 mr-2" />
                          Velg posisjon på kart først
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5 mr-2" />
                          Opprett lokalitet
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setLocationDialogOpen(false);
                        setMarkerPosition(null);
                        setAddressValue('');
                        setNewLocationData({ letter: '', name: '', address: '', latitude: '', longitude: '', area_id: '' });
                        setMapCenter({ lat: 59.25, lng: 5.8 });
                      }} 
                      className="h-14 px-8 rounded-xl border-2 hover:border-red-500 hover:bg-red-50 hover:text-red-700"
                    >
                      Avbryt
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Locations by Area */}
            <div className="space-y-8">
              {areas.map((area) => {
                const areaLocations = getLocationsByArea(area.id).sort((a, b) => a.letter.localeCompare(b.letter));
                
                return (
                  <Card key={area.id} className="shadow-xl border-0 overflow-hidden">
                    {/* Card header with gradient */}
                    <div className="bg-gradient-to-r from-teal-500 to-blue-600 p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                            <MapIcon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-2xl font-bold text-white">{area.name}</h3>
                            <div className="text-sm text-teal-100">{areaLocations.length} lokaliteter</div>
                          </div>
                        </div>
                        <Button
                          onClick={() => {
                            setNewLocationData({ ...newLocationData, area_id: area.id });
                            setLocationDialogOpen(true);
                          }}
                          size="sm"
                          className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border border-white/30"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Legg til lokalitet
                        </Button>
                      </div>
                    </div>
                    
                    <CardContent className="p-6 bg-white">
                      {areaLocations.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          <MapPin className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                          <p>Ingen lokaliteter for dette området</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          {areaLocations.map((location) => (
                            <LocationCard
                              key={location.id}
                              location={location}
                              onEdit={openEditLocationDialog}
                              onDelete={deleteLocation}
                            />
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Edit Location Dialog with Map */}
          <Dialog open={editLocationDialogOpen} onOpenChange={(open) => {
            setEditLocationDialogOpen(open);
            if (!open) {
              setEditMarkerPosition(null);
              setEditAddressValue('');
            }
          }}>
            <DialogContent className="max-w-7xl w-[95vw] max-h-[95vh] overflow-y-auto">
              <DialogHeader className="pb-6 border-b border-slate-200">
                <DialogTitle className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">
                  Rediger lokalitet
                </DialogTitle>
                <p className="text-slate-600 mt-2 text-sm md:text-base">Oppdater informasjon og flytt posisjonen på kartet</p>
              </DialogHeader>
              
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 md:gap-8 py-6">
                {/* Left side: Form fields */}
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-blue-600 rounded-lg text-white text-sm flex items-center justify-center font-bold">1</div>
                      <h3 className="text-lg font-bold text-slate-900">Grunnleggende info</h3>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Bokstav *</Label>
                        <Input
                          value={editLocationData.letter}
                          onChange={(e) => setEditLocationData({ ...editLocationData, letter: e.target.value.toUpperCase() })}
                          placeholder="A"
                          maxLength={1}
                          className="h-14 text-2xl font-bold text-center border-2 border-slate-200 focus:border-teal-500 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Navn *</Label>
                        <Input
                          value={editLocationData.name}
                          onChange={(e) => setEditLocationData({ ...editLocationData, name: e.target.value })}
                          placeholder="f.eks. Toftøy"
                          className="h-14 text-base border-2 border-slate-200 focus:border-teal-500 rounded-xl"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-2 block">Tilhører område *</Label>
                      <Select 
                        value={editLocationData.area_id} 
                        onValueChange={(value) => setEditLocationData({ ...editLocationData, area_id: value })}
                      >
                        <SelectTrigger className="h-14 border-2 border-slate-200 focus:border-teal-500 rounded-xl">
                          <SelectValue placeholder="Velg overvåkingsområde" />
                        </SelectTrigger>
                        <SelectContent>
                          {areas.map((area) => (
                            <SelectItem key={area.id} value={area.id}>
                              <div className="flex items-center space-x-2">
                                <MapIcon className="w-4 h-4 text-blue-600" />
                                <span>{area.name}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Editable Coordinates */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg text-white text-sm flex items-center justify-center font-bold">2</div>
                      <h3 className="text-lg font-bold text-slate-900">Koordinater</h3>
                    </div>
                    
                    <div className="p-6 bg-gradient-to-br from-slate-50 to-blue-50 border-2 border-slate-200 rounded-2xl">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2 block">Breddegrad (Latitude)</Label>
                          <Input
                            type="number"
                            step="0.000001"
                            value={editLocationData.latitude}
                            onChange={(e) => setEditLocationData({ ...editLocationData, latitude: e.target.value })}
                            onBlur={() => {
                              // Jump map to new coordinates when user finishes editing
                              if (editLocationData.latitude && editLocationData.longitude) {
                                setEditMapKey(prev => prev + 1);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && editLocationData.latitude && editLocationData.longitude) {
                                setEditMapKey(prev => prev + 1);
                              }
                            }}
                            placeholder="59.270767"
                            className="h-12 font-mono text-lg border-2 border-slate-200 focus:border-teal-500 rounded-xl"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2 block">Lengdegrad (Longitude)</Label>
                          <Input
                            type="number"
                            step="0.000001"
                            value={editLocationData.longitude}
                            onChange={(e) => setEditLocationData({ ...editLocationData, longitude: e.target.value })}
                            onBlur={() => {
                              // Jump map to new coordinates when user finishes editing
                              if (editLocationData.latitude && editLocationData.longitude) {
                                setEditMapKey(prev => prev + 1);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && editLocationData.latitude && editLocationData.longitude) {
                                setEditMapKey(prev => prev + 1);
                              }
                            }}
                            placeholder="5.691433"
                            className="h-12 font-mono text-lg border-2 border-slate-200 focus:border-teal-500 rounded-xl"
                          />
                        </div>
                      </div>
                      
                      <div className="mt-4 flex items-center justify-between">
                        <div className="flex items-center space-x-2 text-xs text-slate-600">
                          <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                          <span className="font-medium">Kartet hopper automatisk når du endrer koordinater</span>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            // Force map to recenter when clicking this button
                            setEditMapKey(prev => prev + 1);
                          }}
                          className="text-teal-600 hover:text-teal-700 hover:bg-teal-50 border-teal-200"
                          disabled={!editLocationData.latitude || !editLocationData.longitude}
                        >
                          <Navigation className="w-4 h-4 mr-1" />
                          Vis på kart
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Right side: Interactive Map */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg text-white text-sm flex items-center justify-center font-bold">
                        <MapIcon className="w-4 h-4" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900">Flytt posisjon</h3>
                    </div>
                  </div>
                  
                  <div className="relative h-[400px] md:h-[500px] lg:h-[550px] rounded-2xl overflow-hidden border-4 border-slate-200 shadow-2xl">
                    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
                      <GoogleMap
                        key={`edit-map-${editMapKey}`}
                        defaultCenter={editLocationData.latitude && editLocationData.longitude 
                          ? { lat: parseFloat(editLocationData.latitude), lng: parseFloat(editLocationData.longitude) }
                          : { lat: 59.25, lng: 5.8 }}
                        defaultZoom={14}
                        mapId="location-selector-map-edit"
                        onClick={(e) => {
                          if (!e.detail || !e.detail.latLng) return;
                          const lat = e.detail.latLng.lat;
                          const lng = e.detail.latLng.lng;
                          setEditLocationData({
                            ...editLocationData,
                            latitude: lat.toFixed(6),
                            longitude: lng.toFixed(6)
                          });
                        }}
                        gestureHandling="greedy"
                        disableDefaultUI={false}
                        zoomControl={true}
                        mapTypeControl={true}
                        fullscreenControl={true}
                        streetViewControl={false}
                        scrollwheel={true}
                      >
                        {editLocationData.latitude && editLocationData.longitude && (
                          <AdvancedMarker
                            position={{ 
                              lat: parseFloat(editLocationData.latitude), 
                              lng: parseFloat(editLocationData.longitude) 
                            }}
                            draggable={true}
                            onDragEnd={(e) => {
                              const lat = e.latLng.lat();
                              const lng = e.latLng.lng();
                              setEditLocationData({
                                ...editLocationData,
                                latitude: lat.toFixed(6),
                                longitude: lng.toFixed(6)
                              });
                            }}
                          >
                            <div className="relative animate-in zoom-in duration-500">
                              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-600 rounded-full border-4 border-white shadow-2xl flex items-center justify-center cursor-move hover:scale-125 transition-all duration-300">
                                <span className="text-2xl font-bold text-white">{editLocationData.letter}</span>
                              </div>
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-10 bg-gradient-to-b from-teal-600/60 to-transparent rounded-full"></div>
                              <div className="absolute inset-0 w-16 h-16 bg-teal-400 rounded-full animate-ping opacity-30"></div>
                            </div>
                          </AdvancedMarker>
                        )}
                      </GoogleMap>
                    </APIProvider>
                    
                    {/* Instructions overlay */}
                    {(!editLocationData.latitude || !editLocationData.longitude) && (
                      <div className="absolute top-6 left-1/2 transform -translate-x-1/2 z-10 max-w-sm">
                        <div className="bg-white/95 backdrop-blur-md rounded-2xl px-6 py-4 shadow-2xl border-2 border-slate-200">
                          <div className="text-center">
                            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                              <MapPin className="w-6 h-6 text-white" />
                            </div>
                            <p className="text-sm font-semibold text-slate-900 mb-1">Klikk på kartet</p>
                            <p className="text-xs text-slate-600">for å sette posisjon</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Instructions */}
                  <div className="grid grid-cols-2 gap-3 text-center">
                    <div className="p-3 bg-teal-50 rounded-xl border border-teal-200">
                      <div className="text-2xl mb-1">🖱️</div>
                      <div className="text-xs font-semibold text-teal-900">Klikk på kart</div>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-xl border border-purple-200">
                      <div className="text-2xl mb-1">✋</div>
                      <div className="text-xs font-semibold text-purple-900">Dra markør</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Action buttons */}
              <div className="flex space-x-3 pt-6 border-t border-slate-200">
                <Button 
                  onClick={updateLocation} 
                  className="flex-1 h-14 bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white font-bold text-base rounded-xl shadow-lg hover:shadow-xl transition-all"
                  disabled={!editLocationData.letter || !editLocationData.name || !editLocationData.area_id}
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Lagre endringer
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setEditLocationDialogOpen(false)} 
                  className="h-14 px-8 rounded-xl border-2 hover:border-red-500 hover:bg-red-50 hover:text-red-700"
                >
                  Avbryt
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Summary Statistics */}
          <Card className="mt-8 shadow-lg bg-gradient-to-r from-slate-50 to-blue-50">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Oversikt</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900">{areas.length}</div>
                  <div className="text-sm text-slate-600">Områder</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900">{locations.length}</div>
                  <div className="text-sm text-slate-600">Lokaliteter</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900">
                    {locations.filter(l => l.latitude && l.longitude).length}
                  </div>
                  <div className="text-sm text-slate-600">Med koordinater</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900">
                    {new Set(locations.map(l => l.area_id)).size}
                  </div>
                  <div className="text-sm text-slate-600">Aktive områder</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
